import { CommunicationType, getCommunicationType } from '@bariendo/lib/communication';
import { ThemeIcon, Tooltip } from '@mantine/core';
import { Communication } from '@medplum/fhirtypes';
import {
  IconBell,
  IconDeviceMobileMessage,
  IconMessages,
  IconNote,
  IconPhoneIncoming,
  IconPhoneOff,
  IconPrinter,
  IconPhoneOutgoing,
} from '@tabler/icons-react';
import style from '@theme/common.module.css';
import { InboundIcon } from './icons/InboundIcon';
import { ACTIVITY_CATEGORY_COLORS } from './Timeline/activityCategories';

// Activity-timeline canon: ONE color per category, not per individual type.
// SMS / chat / voice — incoming, outgoing, AND failed — all live in the
// Conversations group → Sage. Direction is signaled by the icon shape
// (IconPhone vs IconPhoneCall) and failure by IconPhoneOff. State cues
// (failed / missed / in-progress) live on the in-card status badge, not
// on the marker color — the marker is for category identity, the badge
// is for state.
const CONVERSATIONS_COLOR = ACTIVITY_CATEGORY_COLORS.conversations; // Sage #679773
const NOTE_COLOR = ACTIVITY_CATEGORY_COLORS.notes; // Mustard #C28B2C
const FAX_COLOR = ACTIVITY_CATEGORY_COLORS.careCoordination; // Lavender #7E6CB8

const UNKNOWN_COLOR = '#868E96'; // Mantine gray.6 — categorical fallback

interface CommunicationIconProps {
  communication: Communication | undefined;
  size?: number;
}

export function CommunicationTypeIcon({ communication, size = 14 }: CommunicationIconProps) {
  const communicationType = getCommunicationType(communication as Communication);

  // Canon: 24px circle, Warm White 2px ring, 14px glyph. Color is fully
  // saturated on a colored fill — the ring lets the spine line read clean.
  const actionIconCommonProps = {
    variant: 'filled' as const,
    radius: 'xl' as const,
    size: 24,
    className: style.hoverable,
    styles: { root: { boxShadow: '0 0 0 2px #FAFCF7' } },
  };

  switch (communicationType) {
    case CommunicationType.VOICE_CALL_INCOMING:
      return (
        <Tooltip label="Incoming call" position="top" withArrow>
          <ThemeIcon {...actionIconCommonProps} bg={CONVERSATIONS_COLOR} aria-label="Incoming call" role="img">
            <IconPhoneIncoming size={size} />
          </ThemeIcon>
        </Tooltip>
      );
    case CommunicationType.VOICE_CALL_OUTGOING:
      return (
        <Tooltip label="Outgoing call" position="top" withArrow>
          <ThemeIcon {...actionIconCommonProps} bg={CONVERSATIONS_COLOR} aria-label="Outgoing call" role="img">
            <IconPhoneOutgoing size={size} />
          </ThemeIcon>
        </Tooltip>
      );
    case CommunicationType.VOICE_CALL_FAILED:
      return (
        <Tooltip label="Failed call" position="top" withArrow>
          <ThemeIcon {...actionIconCommonProps} bg={CONVERSATIONS_COLOR} aria-label="Failed call" role="img">
            <IconPhoneOff size={size} />
          </ThemeIcon>
        </Tooltip>
      );
    case CommunicationType.TEXT_MESSAGE_INCOMING:
      return (
        <Tooltip label="Received SMS" position="top" withArrow>
          <ThemeIcon {...actionIconCommonProps} bg={CONVERSATIONS_COLOR} aria-label="Received SMS" role="img">
            <IconDeviceMobileMessage size={size} />
          </ThemeIcon>
        </Tooltip>
      );
    case CommunicationType.TEXT_MESSAGE_OUTGOING:
      return (
        <Tooltip label="Sent SMS" position="top" withArrow>
          <ThemeIcon {...actionIconCommonProps} bg={CONVERSATIONS_COLOR} title="Sent SMS">
            <IconDeviceMobileMessage size={size} />
          </ThemeIcon>
        </Tooltip>
      );
    case CommunicationType.CHAT_MESSAGE_INCOMING:
      return (
        <Tooltip label="Received message" position="top" withArrow>
          <ThemeIcon {...actionIconCommonProps} bg={CONVERSATIONS_COLOR} title="Received message">
            <IconMessages size={size} />
          </ThemeIcon>
        </Tooltip>
      );
    case CommunicationType.CHAT_MESSAGE_OUTGOING:
      return (
        <Tooltip label="Sent message" position="top" withArrow>
          <ThemeIcon {...actionIconCommonProps} bg={CONVERSATIONS_COLOR}>
            <IconMessages size={size} />
          </ThemeIcon>
        </Tooltip>
      );
    case CommunicationType.STANDALONE_NOTE:
    case CommunicationType.TIMELINE_NOTE:
      return (
        <Tooltip label="Note" position="top" withArrow>
          <ThemeIcon {...actionIconCommonProps} bg={NOTE_COLOR} title="Note">
            <IconNote size={size} />
          </ThemeIcon>
        </Tooltip>
      );
    case CommunicationType.FAX_INCOMING:
      return (
        <Tooltip label="Inbound fax" position="top" withArrow>
          <ThemeIcon {...actionIconCommonProps} bg={FAX_COLOR} title="Inbound fax">
            <InboundIcon size={size} />
          </ThemeIcon>
        </Tooltip>
      );
    case CommunicationType.FAX_OUTGOING:
      return (
        <Tooltip label="Sent fax" position="top" withArrow>
          <ThemeIcon {...actionIconCommonProps} bg={FAX_COLOR} aria-label="Sent fax" role="img">
            <IconPrinter size={16} />
          </ThemeIcon>
        </Tooltip>
      );
    case CommunicationType.UNKNOWN:
    default:
      return (
        <Tooltip label="Unknown" position="top" withArrow>
          <ThemeIcon {...actionIconCommonProps} bg={UNKNOWN_COLOR}>
            <IconBell size={size} />
          </ThemeIcon>
        </Tooltip>
      );
  }
}
